import { useState, useRef } from "react";
import { useStore, CATEGORIES, CAT_META, HOME_SECTIONS, VIDEO_ASPECTS } from "../store/Store";

const BADGE_OPTIONS = ["","New","Hot","Trending","Bestseller","Premium","Sale"];
const ACCENT_OPTIONS = ["#EC4899","#7C3AED","#F59E0B","#10B981","#0EA5E9","#EF4444","#8B5CF6","#06B6D4"];
const STYLE_OPTIONS = [
  { id:"wave",    l:"🌊 Wave Block (bold colored block, photo emerges from a wave cut)" },
  { id:"newin",   l:"🆕 New In (solid color card, straight photo, simple caption)" },
  { id:"strip",   l:"▬ Clean Strip (minimal white card, slim colored top bar)" },
  { id:"tag",     l:"🎬 Editorial Tag (full photo, dark gradient, magazine style)" },
  { id:"grid",    l:"▦ Classic Grid (standard product card)" },
  { id:"trendy",  l:"🏷️ Trendy (colored header bar, uncropped photo)" },
  { id:"polaroid",l:"📸 Polaroid (instant-photo look, tilted on hover)" },
  { id:"split",   l:"◤ Split (diagonal color/white divide)" },
  { id:"outline", l:"▢ Outline (minimal, transparent, gallery style)" },
  { id:"circle",  l:"⬤ Circle Badge (round photo on pastel square)" },
  { id:"bloom",   l:"✿ Bloom (floral corners, soft romantic feel)" },
];

export default function AdminPanel({ onNav }) {
  const { admin, signOut, products, setProducts, banners, setBanners, catStrip, setCatStrip, catPhotos, setCatPhotos, orders, setOrders, featuredIds, setFeaturedIds, homeCollections, setHomeCollections, occasionTiles, setOccasionTiles, sectionVis, setSectionVis, kurtiStyleTiles, setKurtiStyleTiles, categories, catMetaExtra, addCategory, removeCategory, coupons, setCoupons, announcements, setAnnouncements, heroPhotoDesktop, setHeroPhotoDesktop, heroPhotoMobile, setHeroPhotoMobile, logoUrl, setLogoUrl, adminWhatsApp, setAdminWhatsApp, freeShipThreshold, setFreeShipThreshold, shippingFee, setShippingFee, videoShowcases, setVideoShowcases, getAccessToken, notify } = useStore();
  const [customers, setCustomers] = useState(null);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState(null);

  const loadCustomers = async () => {
    setCustomersLoading(true);
    setCustomersError(null);
    try {
      const token = await getAccessToken();
      const res = await fetch("https://hmgeuxrzfvtmakzgpqub.supabase.co/functions/v1/list-customers", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load customers");
      setCustomers(data.customers);
    } catch (e) {
      setCustomersError(String(e.message || e));
    } finally {
      setCustomersLoading(false);
    }
  };
  const getMeta = (cat) => CAT_META[cat] || catMetaExtra[cat] || { emoji:"👗", color:"#EC4899", light:"#FDF2F8" };
  const [tab, setTab] = useState("dashboard");
  const [showForm, setShowForm] = useState(false);
  const [editP, setEditP] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [orderSearchQ, setOrderSearchQ] = useState("");
  const [bannerEdit, setBannerEdit] = useState(null);
  const [form, setForm] = useState({ name:"", category:CATEGORIES[0], price:"", mrp:"", stock:"", badge:"", desc:"", material:"", care:"", colors:"", sizes:"", img:"", images:"", colorImages:"", sizeStock:{} });
  const [expandedCol, setExpandedCol] = useState(null);
  const [newCat, setNewCat] = useState({ name:"", emoji:"👗", color:"#EC4899" });
  const [newCoupon, setNewCoupon] = useState({ code:"", type:"percent", value:"", minOrder:"", maxDiscount:"", label:"" });
  const [newAnnouncement, setNewAnnouncement] = useState("");
  const [newVideo, setNewVideo] = useState({ videoUrl:"", productId:"", aspectRatio:"portrait", placement:"home:featured" });
  const csvInputRef = useRef(null);

  if (!admin) { onNav("adminlogin"); return null; }

  const resetForm = () => { setForm({ name:"", category:CATEGORIES[0], price:"", mrp:"", stock:"", badge:"", desc:"", material:"", care:"", colors:"", sizes:"", img:"", images:"", colorImages:"", sizeStock:{} }); setEditP(null); setShowForm(false); };

  const openEdit = p => {
    const galleryList = Array.isArray(p.images) && p.images.length ? p.images : (p.img ? [p.img] : []);
    const sizesList = Array.isArray(p.sizes)?p.sizes:[];
    let sizeStock = p.sizeStock||{};
    // Old products edited before per-size stock existed: don't silently zero out their stock —
    // spread the existing total evenly across sizes as an editable starting point.
    if (sizesList.length && Object.keys(sizeStock).length===0 && p.stock>0) {
      const base = Math.floor(p.stock / sizesList.length);
      let remainder = p.stock - base*sizesList.length;
      sizeStock = Object.fromEntries(sizesList.map(sz=>{
        const v = base + (remainder>0 ? 1 : 0);
        if (remainder>0) remainder--;
        return [sz, v];
      }));
    }
    setForm({
      name:p.name, category:p.category, price:p.price, mrp:p.mrp, stock:p.stock, badge:p.badge||"",
      desc:p.desc||"", material:p.material||"", care:p.care||"",
      colors:Array.isArray(p.colors)?p.colors.join(", "):"",
      sizes:sizesList.join(", "),
      img:p.img||"",
      images:galleryList.join("\n"),
      colorImages:Object.entries(p.colorImages||{}).map(([c,u])=>`${c}|${u}`).join("\n"),
      sizeStock,
    });
    setEditP(p); setShowForm(true);
  };

  const sizesArr = form.sizes.split(",").map(s=>s.trim()).filter(Boolean);

  // Simple CSV parser — supports quoted fields with commas inside them.
  const parseCsv = (text) => {
    const rows = [];
    let row = [], field = "", inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i], next = text[i+1];
      if (inQuotes) {
        if (c === '"' && next === '"') { field += '"'; i++; }
        else if (c === '"') { inQuotes = false; }
        else field += c;
      } else {
        if (c === '"') inQuotes = true;
        else if (c === ',') { row.push(field); field = ""; }
        else if (c === '\n' || c === '\r') {
          if (field.length || row.length) { row.push(field); rows.push(row); row = []; field = ""; }
        } else field += c;
      }
    }
    if (field.length || row.length) { row.push(field); rows.push(row); }
    return rows.filter(r => r.some(c=>c.trim()));
  };

  const downloadCsvTemplate = () => {
    const header = "name,category,price,mrp,stock,badge,material,care,colors,sizes,img,description";
    const sample = `"Sample Anarkali Kurti","Kurti",1299,1800,20,"New","Rayon","Machine Wash","Red|Blue|Green","S|M|L|XL","https://example.com/photo.jpg","A lovely anarkali kurti."`;
    const blob = new Blob([header + "\n" + sample], { type:"text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "matchwell-product-template.csv";
    a.click();
  };

  const csvField = (v) => `"${String(v??"").replace(/"/g,'""')}"`;
  const exportProductsCsv = () => {
    const header = "name,category,price,mrp,stock,badge,material,care,colors,sizes,img,description";
    const rows = products.map(p => [
      p.name, p.category, p.price, p.mrp, p.stock, p.badge||"", p.material||"", p.care||"",
      (p.colors||[]).join("|"), (p.sizes||[]).join("|"), p.img||"", p.desc||p.description||"",
    ].map(csvField).join(","));
    const blob = new Blob([header + "\n" + rows.join("\n")], { type:"text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `matchwell-products-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const handleCsvFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const rows = parseCsv(e.target.result);
      if (rows.length < 2) { notify("CSV khaali ya galat format hai", "err"); return; }
      const header = rows[0].map(h=>h.trim().toLowerCase());
      const idx = (name) => header.indexOf(name);
      const newProducts = rows.slice(1).map((r, i) => ({
        id: Date.now() + i,
        name: r[idx("name")]?.trim() || "Untitled",
        category: r[idx("category")]?.trim() || CATEGORIES[0],
        price: Number(r[idx("price")]) || 0,
        mrp: Number(r[idx("mrp")]) || Number(r[idx("price")]) || 0,
        stock: Number(r[idx("stock")]) || 0,
        badge: r[idx("badge")]?.trim() || "",
        material: r[idx("material")]?.trim() || "",
        care: r[idx("care")]?.trim() || "",
        colors: (r[idx("colors")]||"").split("|").map(s=>s.trim()).filter(Boolean),
        sizes: (r[idx("sizes")]||"").split("|").map(s=>s.trim()).filter(Boolean),
        img: r[idx("img")]?.trim() || "",
        images: r[idx("img")]?.trim() ? [r[idx("img")].trim()] : [],
        desc: r[idx("description")]?.trim() || "",
        colorImages: {}, sizeStock: {},
        rating: 4.5, reviews: 0,
      })).filter(p => p.name && p.name !== "Untitled");

      if (!newProducts.length) { notify("Koi valid product row nahi mili", "err"); return; }
      if (window.confirm(`${newProducts.length} products import karein?`)) {
        setProducts(ps => [...ps, ...newProducts]);
        notify(`${newProducts.length} products add ho gaye!`);
      }
    };
    reader.readAsText(file);
  };


  const saveP = () => {
    if (!form.name||!form.price) { notify("Name & price required","err"); return; }
    const colorImages = Object.fromEntries(
      form.colorImages.split("\n").map(line=>line.split("|").map(s=>s.trim())).filter(([c,u])=>c&&u)
    );
    const images = form.images.split("\n").map(s=>s.trim()).filter(Boolean).slice(0,6);
    if (!images.length && form.img) images.push(form.img);

    let sizeStock = {};
    let totalStock;
    if (sizesArr.length) {
      sizeStock = Object.fromEntries(sizesArr.map(s=>[s, Number(form.sizeStock[s])||0]));
      totalStock = Object.values(sizeStock).reduce((a,b)=>a+b, 0);
    } else {
      totalStock = Number(form.stock)||0;
    }

    const data = {
      ...form, price:+form.price, mrp:+(form.mrp||form.price), stock:totalStock,
      colors:form.colors.split(",").map(c=>c.trim()).filter(Boolean),
      sizes:sizesArr,
      colorImages, images, sizeStock,
      img: images[0] || "",
      rating:editP?.rating||4.5, reviews:editP?.reviews||0,
    };
    if (editP) setProducts(ps=>ps.map(p=>p.id===editP.id?{...data,id:editP.id}:p));
    else setProducts(ps=>[...ps,{...data,id:Date.now()}]);
    notify(editP?"Product updated!":"Product added!"); resetForm();
  };

  const addCollection = () => {
    setHomeCollections(cs=>[...cs, { id:Date.now(), title:"New Collection", active:true, accent:ACCENT_OPTIONS[cs.length%ACCENT_OPTIONS.length], style:"wave", productIds:[] }]);
  };
  const toggleColProduct = (colId, pid) => {
    setHomeCollections(cs=>cs.map(c=>c.id===colId?{...c, productIds: c.productIds.includes(pid)?c.productIds.filter(x=>x!==pid):[...c.productIds,pid]}:c));
  };

  const revenue = orders.reduce((s,o)=>s+(o.total||0), 0);
  const filtered = products.filter(p=>p.name?.toLowerCase().includes(searchQ.toLowerCase())||p.category?.toLowerCase().includes(searchQ.toLowerCase()));

  const TABS = [
    {id:"dashboard",  emoji:"📊",l:"Dashboard"},
    {id:"sections",   emoji:"👁️",l:"Homepage Sections"},
    {id:"sales",      emoji:"📈",l:"Sales"},
    {id:"products",   emoji:"📦",l:"Products"},
    {id:"categories", emoji:"🏷️",l:"Categories"},
    {id:"coupons",    emoji:"🎟️",l:"Coupons"},
    {id:"announce",   emoji:"📢",l:"Announcement Bar"},
    {id:"videos",     emoji:"🎬",l:"Videos"},
    {id:"featured",   emoji:"⭐",l:"Featured"},
    {id:"collections",emoji:"🧵",l:"Collections"},
    {id:"banners",    emoji:"🖼️",l:"Banners"},
    {id:"catstrip",   emoji:"🎠",l:"Category Strip"},
    {id:"occasion",   emoji:"🎉",l:"Occasion"},
    {id:"kurtistyle", emoji:"👗",l:"Kurti Styles"},
    {id:"orders",     emoji:"🛒",l:"Orders"},
    {id:"customers",  emoji:"👥",l:"Customers"},
    {id:"payment",    emoji:"💳",l:"Payment"},
    {id:"staff",      emoji:"👥",l:"Staff"},
  ];

  const inp = { width:"100%", padding:"9px 13px", border:"1px solid #e5e5e5", borderRadius:10, fontSize:13, outline:"none", background:"#fafafa", fontFamily:"'DM Sans',sans-serif" };
  const lbl = { fontSize:9, letterSpacing:1, textTransform:"uppercase", color:"#999", display:"block", marginBottom:4, fontWeight:700 };

  return (
    <div style={{ minHeight:"100vh", background:"#f8f9fa", fontFamily:"'DM Sans',sans-serif" }}>
      {/* Admin Nav */}
      <div style={{ background:"#1a1a2e", height:56, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 20px", position:"fixed", top:0, left:0, right:0, zIndex:1000 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontFamily:"'Noto Serif Devanagari',serif", fontSize:18, fontWeight:900, color:"#FF3333" }}>मॅचवेल</span>
          <span style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", background:"linear-gradient(135deg,#EC4899,#7C3AED)", color:"white", padding:"3px 12px", borderRadius:20, fontWeight:700 }}>Admin</span>
        </div>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <span style={{ fontSize:12, color:"rgba(255,255,255,0.6)" }}>👤 {admin.name}</span>
          <button onClick={()=>onNav("home")} style={{ background:"rgba(255,255,255,0.1)", border:"none", color:"white", padding:"6px 14px", borderRadius:20, fontSize:11, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>View Site</button>
          <button onClick={()=>{signOut();onNav("home");}} style={{ background:"#EF4444", border:"none", color:"white", padding:"6px 14px", borderRadius:20, fontSize:11, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Logout</button>
        </div>
      </div>

      <div style={{ display:"flex", paddingTop:56, minHeight:"100vh" }}>
        {/* Sidebar */}
        <div style={{ width:190, background:"white", borderRight:"1px solid #f0f0f0", padding:"16px 0", flexShrink:0, position:"sticky", top:56, height:"calc(100vh - 56px)", overflowY:"auto" }}>
          {TABS.map(t=>(
            <div key={t.id} onClick={()=>setTab(t.id)}
              style={{ padding:"11px 18px", cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", gap:8, color:tab===t.id?"#EC4899":"#666", background:tab===t.id?"#FDF2F8":"transparent", borderLeft:tab===t.id?"3px solid #EC4899":"3px solid transparent", fontWeight:tab===t.id?700:400, transition:"all 0.2s" }}>
              {t.emoji} {t.l}
            </div>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex:1, padding:22, overflowY:"auto" }}>

          {/* DASHBOARD */}
          {tab==="dashboard"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:20 }}>Dashboard</h2>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:14, marginBottom:28 }}>
                {[{l:"Products",v:products.length,emoji:"📦",bg:"linear-gradient(135deg,#667eea,#764ba2)"},{l:"Orders",v:orders.length,emoji:"🛒",bg:"linear-gradient(135deg,#f093fb,#f5576c)"},{l:"Revenue",v:`₹${revenue.toLocaleString("en-IN")}`,emoji:"💰",bg:"linear-gradient(135deg,#4facfe,#00f2fe)"},{l:"Low Stock",v:products.filter(p=>p.stock>0&&p.stock<=5).length,emoji:"⚠️",bg:"linear-gradient(135deg,#43e97b,#38f9d7)"}].map((s,i)=>(
                  <div key={i} style={{ background:s.bg, borderRadius:16, padding:18, color:"white" }}>
                    <div style={{ fontSize:26, marginBottom:6 }}>{s.emoji}</div>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:900 }}>{s.v}</div>
                    <div style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", opacity:0.8, marginTop:3 }}>{s.l}</div>
                  </div>
                ))}
              </div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:17, fontWeight:700, color:"#1a1a2e", marginBottom:12 }}>Recent Orders</h3>
              {orders.length===0?<p style={{ color:"#999", fontSize:13 }}>No orders yet.</p>:orders.slice(0,5).map(o=>(
                <div key={o.id} style={{ background:"white", borderRadius:12, padding:14, marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", boxShadow:"0 1px 8px rgba(0,0,0,0.05)" }}>
                  <div><div style={{ fontWeight:700, fontSize:13 }}>{o.id}</div><div style={{ fontSize:11, color:"#999" }}>{o.addr?.name} · {o.date}</div></div>
                  <div style={{ textAlign:"right" }}><div style={{ fontWeight:800, color:"#1a1a2e" }}>₹{o.total?.toLocaleString("en-IN")}</div><span style={{ fontSize:10, background:"#F0FDF4", color:"#16A34A", padding:"2px 8px", borderRadius:20, fontWeight:700 }}>{o.status}</span></div>
                </div>
              ))}
            </div>
          )}

          {/* HOMEPAGE SECTIONS — master show/hide toggle for everything on the homepage */}
          {tab==="sections"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:8 }}>Homepage Sections</h2>
              <p style={{ fontSize:13, color:"#999", marginBottom:20 }}>Homepage par jitne bhi sections hain, unhe yaha se ek jagah show/hide kar sakte ho — order upar-se-niche wahi hai jaisa homepage par dikhta hai.</p>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {HOME_SECTIONS.map((s,i)=>{
                  const isOn = sectionVis[s.key] !== false;
                  return (
                    <div key={s.key} style={{ background:"white", borderRadius:12, padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between", boxShadow:"0 2px 10px rgba(0,0,0,0.04)", border:`1px solid ${isOn?"#f0f0f0":"#FECACA"}` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                        <span style={{ fontSize:11, color:"#ccc", fontWeight:700, width:20 }}>{i+1}</span>
                        <span style={{ fontSize:13.5, fontWeight:600, color:"#1a1a2e" }}>{s.label}</span>
                      </div>
                      <button onClick={()=>setSectionVis(v=>({...v,[s.key]:!isOn}))}
                        style={{ padding:"6px 16px", background:isOn?"#F0FDF4":"#FEF2F2", color:isOn?"#16A34A":"#EF4444", border:"none", borderRadius:20, fontSize:11.5, fontWeight:700, cursor:"pointer", minWidth:96 }}>
                        {isOn?"✓ Showing":"✗ Hidden"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* SALES */}
          {tab==="sales"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:20 }}>Sales Report</h2>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))", gap:14, marginBottom:24 }}>
                {[{l:"Revenue",v:`₹${revenue.toLocaleString("en-IN")}`,c:"#EC4899"},{l:"Orders",v:orders.length,c:"#7C3AED"},{l:"Avg Order",v:orders.length?`₹${Math.round(revenue/orders.length).toLocaleString("en-IN")}`:"₹0",c:"#0EA5E9"},{l:"Items Sold",v:orders.reduce((s,o)=>s+(o.items?.reduce((a,i)=>a+i.qty,0)||0),0),c:"#10B981"}].map((s,i)=>(
                  <div key={i} style={{ background:"white", borderRadius:14, padding:18, boxShadow:"0 2px 12px rgba(0,0,0,0.05)", borderTop:`4px solid ${s.c}` }}>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:900, color:s.c }}>{s.v}</div>
                    <div style={{ fontSize:10, letterSpacing:1, textTransform:"uppercase", color:"#999", marginTop:3 }}>{s.l}</div>
                  </div>
                ))}
              </div>

              {/* Revenue trend — last 14 days, no chart library needed */}
              {(() => {
                const days = [...Array(14)].map((_,i)=>{
                  const d = new Date(Date.now() - (13-i)*86400000);
                  const key = d.toLocaleDateString("en-IN");
                  const dayRev = orders.filter(o=>o.date===key).reduce((s,o)=>s+(o.total||0),0);
                  return { label: d.toLocaleDateString("en-IN",{day:"2-digit",month:"short"}), rev: dayRev };
                });
                const maxRev = Math.max(...days.map(d=>d.rev), 1);
                const w = 560, h = 140, gap = w/days.length;
                const points = days.map((d,i)=> `${i*gap + gap/2},${h - (d.rev/maxRev)*h}`).join(" ");
                return (
                  <div style={{ background:"white", borderRadius:14, padding:18, boxShadow:"0 2px 12px rgba(0,0,0,0.05)", marginBottom:22, overflowX:"auto" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                      <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:"#1a1a2e" }}>Revenue — Last 14 Days</h3>
                      <button onClick={()=>{
                        const rows = [["Order ID","Date","Customer","Phone","Total","Discount","Payment","Status"]];
                        orders.forEach(o=>rows.push([o.id,o.date,o.addr?.name||"",o.addr?.phone||"",o.total,o.discount||0,o.pay,o.status]));
                        const csv = rows.map(r=>r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
                        const blob = new Blob([csv], { type:"text/csv" });
                        const a = document.createElement("a");
                        a.href = URL.createObjectURL(blob);
                        a.download = `matchwell-orders-${new Date().toISOString().slice(0,10)}.csv`;
                        a.click();
                      }} style={{ fontSize:11, fontWeight:700, color:"#1a1a2e", background:"#fafafa", border:"1px solid #e5e5e5", padding:"7px 16px", borderRadius:20, cursor:"pointer" }}>
                        ⬇ Export Orders CSV
                      </button>
                    </div>
                    <svg width="100%" height={h+30} viewBox={`0 0 ${w} ${h+30}`} preserveAspectRatio="none" style={{ minWidth:400 }}>
                      <polyline points={points} fill="none" stroke="#EC4899" strokeWidth="2.5"/>
                      {days.map((d,i)=>(
                        <circle key={i} cx={i*gap+gap/2} cy={h-(d.rev/maxRev)*h} r="3" fill="#EC4899"/>
                      ))}
                      {days.map((d,i)=> i%2===0 && (
                        <text key={i} x={i*gap+gap/2} y={h+18} fontSize="9" fill="#999" textAnchor="middle">{d.label}</text>
                      ))}
                    </svg>
                  </div>
                );
              })()}
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:17, fontWeight:700, color:"#1a1a2e", marginBottom:14 }}>By Category</h3>
              <div style={{ background:"white", borderRadius:14, padding:18, boxShadow:"0 2px 12px rgba(0,0,0,0.05)", marginBottom:22 }}>
                {categories.map(cat=>{
                  const catRev = orders.flatMap(o=>o.items||[]).filter(i=>i.category===cat).reduce((s,i)=>s+i.price*i.qty,0);
                  const pct = revenue ? Math.round((catRev/revenue)*100) : 0;
                  const m = getMeta(cat);
                  return (
                    <div key={cat} style={{ marginBottom:12 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                        <span style={{ fontSize:13, fontWeight:600 }}>{m.emoji} {cat}</span>
                        <span style={{ fontSize:12, fontWeight:700, color:m.color }}>₹{catRev.toLocaleString("en-IN")} ({pct}%)</span>
                      </div>
                      <div style={{ height:7, background:"#f0f0f0", borderRadius:4, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${pct}%`, background:m.color, borderRadius:4 }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Update order status */}
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:17, fontWeight:700, color:"#1a1a2e", marginBottom:12 }}>Update Order Status</h3>
              {orders.length===0?<p style={{ color:"#999", fontSize:13 }}>No orders.</p>:orders.map(o=>(
                <div key={o.id} style={{ background:"white", borderRadius:12, padding:14, marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10, boxShadow:"0 1px 8px rgba(0,0,0,0.05)" }}>
                  <div><div style={{ fontWeight:700, fontSize:13 }}>{o.id}</div><div style={{ fontSize:11, color:"#999" }}>{o.addr?.name} · ₹{o.total?.toLocaleString("en-IN")}</div></div>
                  <select value={o.status} onChange={e=>setOrders(os=>os.map(x=>x.id===o.id?{...x,status:e.target.value}:x))}
                    style={{ padding:"7px 12px", border:"1px solid #e5e5e5", borderRadius:8, fontSize:12, outline:"none", cursor:"pointer", background:"white", fontFamily:"'DM Sans',sans-serif" }}>
                    {["Confirmed","Packed","Shipped","Out for Delivery","Delivered","Cancelled","Returned"].map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}

          {/* PRODUCTS */}
          {tab==="products"&&(
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18, flexWrap:"wrap", gap:10 }}>
                <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e" }}>Products ({products.length})</h2>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} placeholder="Search..." style={{ padding:"8px 14px", border:"1px solid #e5e5e5", borderRadius:50, fontSize:12, outline:"none", width:160 }}/>
                  <button onClick={downloadCsvTemplate} style={{ background:"white", color:"#1a1a2e", border:"1px solid #e5e5e5", padding:"8px 16px", borderRadius:50, fontSize:12, fontWeight:700, cursor:"pointer" }}>📥 CSV Template</button>
                  <button onClick={exportProductsCsv} style={{ background:"white", color:"#1a1a2e", border:"1px solid #e5e5e5", padding:"8px 16px", borderRadius:50, fontSize:12, fontWeight:700, cursor:"pointer" }}>⬇ Export Products</button>
                  <button onClick={()=>csvInputRef.current?.click()} style={{ background:"white", color:"#1a1a2e", border:"1px solid #e5e5e5", padding:"8px 16px", borderRadius:50, fontSize:12, fontWeight:700, cursor:"pointer" }}>📤 Import CSV</button>
                  <input ref={csvInputRef} type="file" accept=".csv" style={{ display:"none" }} onChange={e=>{ if(e.target.files[0]) handleCsvFile(e.target.files[0]); e.target.value=""; }}/>
                  <button onClick={()=>{resetForm();setShowForm(true);}} style={{ background:"linear-gradient(135deg,#EC4899,#7C3AED)", color:"white", border:"none", padding:"8px 18px", borderRadius:50, fontSize:12, fontWeight:700, cursor:"pointer" }}>+ Add Product</button>
                </div>
              </div>

              {showForm&&(
                <div style={{ background:"white", borderRadius:16, padding:22, marginBottom:18, boxShadow:"0 4px 24px rgba(0,0,0,0.08)", border:"1px solid #f0f0f0" }}>
                  <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:19, fontWeight:700, color:"#1a1a2e", marginBottom:18 }}>{editP?"Edit Product":"Add Product"}</h3>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(190px,1fr))", gap:12 }}>
                    <div style={{ gridColumn:"1/-1" }}><label style={lbl}>Product Name *</label><input style={inp} value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Product name"/></div>
                    <div><label style={lbl}>Category *</label>
                      <select style={inp} value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
                        {categories.map(c=><option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div><label style={lbl}>Badge</label>
                      <select style={inp} value={form.badge} onChange={e=>setForm({...form,badge:e.target.value})}>
                        {BADGE_OPTIONS.map(b=><option key={b} value={b}>{b||"No Badge"}</option>)}
                      </select>
                    </div>
                    <div><label style={lbl}>Selling Price (₹) *</label><input style={inp} type="number" value={form.price} onChange={e=>setForm({...form,price:e.target.value})} placeholder="1299"/></div>
                    <div><label style={lbl}>MRP (₹)</label><input style={inp} type="number" value={form.mrp} onChange={e=>setForm({...form,mrp:e.target.value})} placeholder="1800"/></div>
                    {!sizesArr.length && (
                      <div><label style={lbl}>Stock *</label><input style={inp} type="number" value={form.stock} onChange={e=>setForm({...form,stock:e.target.value})} placeholder="25"/></div>
                    )}
                    <div><label style={lbl}>Material</label><input style={inp} value={form.material} onChange={e=>setForm({...form,material:e.target.value})} placeholder="100% Cotton"/></div>
                    <div><label style={lbl}>Care</label><input style={inp} value={form.care} onChange={e=>setForm({...form,care:e.target.value})} placeholder="Machine Wash"/></div>
                    <div style={{ gridColumn:"1/-1" }}>
                      <label style={lbl}>Product Photos (up to 6 — one URL per line. First = main/cover photo)</label>
                      <textarea style={{...inp, height:110, resize:"none"}} value={form.images} onChange={e=>setForm({...form,images:e.target.value})}
                        placeholder={"https://... (front)\nhttps://... (back)\nhttps://... (left side)\nhttps://... (right side)\nhttps://... (close-up)\nhttps://... (worn/model)"}/>
                      {form.images.split("\n").filter(s=>s.trim()).length > 6 && (
                        <div style={{ fontSize:10, color:"#EF4444", marginTop:4, fontWeight:600 }}>Sirf pehli 6 photos save hongi.</div>
                      )}
                    </div>
                    <div style={{ gridColumn:"1/-1" }}><label style={lbl}>Colors (comma separated)</label><input style={inp} value={form.colors} onChange={e=>setForm({...form,colors:e.target.value})} placeholder="Red, Blue, Green"/></div>
                    <div style={{ gridColumn:"1/-1" }}>
                      <label style={lbl}>Color Photos (optional — one per line: ColorName|ImageURL)</label>
                      <textarea style={{...inp, height:80, resize:"none"}} value={form.colorImages} onChange={e=>setForm({...form,colorImages:e.target.value})} placeholder={"Sage|https://...\nBeige|https://...\nDusty Rose|https://..."}/>
                    </div>
                    <div style={{ gridColumn:"1/-1" }}><label style={lbl}>Sizes (comma separated)</label><input style={inp} value={form.sizes} onChange={e=>setForm({...form,sizes:e.target.value})} placeholder="S, M, L, XL"/></div>
                    {sizesArr.length > 0 && (
                      <div style={{ gridColumn:"1/-1" }}>
                        <label style={lbl}>Stock per Size *</label>
                        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                          {sizesArr.map(sz=>(
                            <div key={sz} style={{ display:"flex", alignItems:"center", gap:6, background:"#fafafa", border:"1px solid #e5e5e5", borderRadius:10, padding:"6px 10px" }}>
                              <span style={{ fontSize:11, fontWeight:700, color:"#1a1a2e", minWidth:28 }}>{sz}</span>
                              <input type="number" min={0} value={form.sizeStock[sz] ?? ""} placeholder="0"
                                onChange={e=>setForm({...form, sizeStock:{...form.sizeStock, [sz]:e.target.value}})}
                                style={{ width:56, padding:"5px 6px", border:"1px solid #e5e5e5", borderRadius:6, fontSize:12, outline:"none" }}/>
                            </div>
                          ))}
                        </div>
                        <div style={{ fontSize:10, color:"#999", marginTop:6 }}>
                          Total stock: <strong style={{ color:"#1a1a2e" }}>{sizesArr.reduce((sum,sz)=>sum+(Number(form.sizeStock[sz])||0),0)}</strong>
                        </div>
                      </div>
                    )}
                    <div style={{ gridColumn:"1/-1" }}><label style={lbl}>Description</label><textarea style={{ ...inp, height:72, resize:"none" }} value={form.desc} onChange={e=>setForm({...form,desc:e.target.value})} placeholder="Product description..."/></div>
                  </div>
                  {form.images.split("\n").map(s=>s.trim()).filter(Boolean).length > 0 && (
                    <div style={{ marginTop:10, display:"flex", gap:8, flexWrap:"wrap" }}>
                      {form.images.split("\n").map(s=>s.trim()).filter(Boolean).slice(0,6).map((url,i)=>(
                        <div key={i} style={{ position:"relative" }}>
                          <img src={url} alt="" style={{ height:80, width:64, borderRadius:10, objectFit:"cover", border:i===0?"2px solid #EC4899":"1px solid #e5e5e5" }} onError={e=>e.target.style.opacity=0.15}/>
                          {i===0 && <span style={{ position:"absolute", bottom:2, left:2, background:"#EC4899", color:"white", fontSize:8, fontWeight:700, padding:"1px 5px", borderRadius:6 }}>Cover</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display:"flex", gap:10, marginTop:14 }}>
                    <button onClick={saveP} style={{ background:"linear-gradient(135deg,#EC4899,#7C3AED)", color:"white", border:"none", padding:"10px 26px", borderRadius:50, fontSize:13, fontWeight:700, cursor:"pointer" }}>{editP?"Update":"Add"}</button>
                    <button onClick={resetForm} style={{ background:"white", color:"#666", border:"1px solid #e5e5e5", padding:"10px 22px", borderRadius:50, fontSize:12, cursor:"pointer" }}>Cancel</button>
                  </div>
                </div>
              )}

              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:14 }}>
                {filtered.map(p=>{
                  const meta = CAT_META[p.category]||{color:"#EC4899",light:"#FDF2F8",emoji:"👗"};
                  return (
                    <div key={p.id} style={{ background:"white", borderRadius:14, overflow:"hidden", boxShadow:"0 2px 12px rgba(0,0,0,0.06)", border:`1px solid ${meta.color}22` }}>
                      <div style={{ height:140, background:p.img?"white":meta.light, position:"relative", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center" }}>
                        {p.img?<img src={p.img} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }} alt="" onError={e=>e.target.style.display="none"}/>:<span style={{ fontSize:36, opacity:0.2 }}>{meta.emoji}</span>}
                        {p.badge&&<span style={{ position:"absolute", top:8, left:8, background:meta.color, color:"white", fontSize:8, fontWeight:700, padding:"2px 8px", borderRadius:20 }}>{p.badge}</span>}
                        <span style={{ position:"absolute", top:8, right:8, background:p.stock===0?"#EF4444":p.stock<=5?"#F59E0B":"#10B981", color:"white", fontSize:8, fontWeight:700, padding:"2px 8px", borderRadius:20 }}>
                          {p.stock===0?"Out":p.stock<=5?`${p.stock} left`:`${p.stock}`}
                        </span>
                      </div>
                      <div style={{ padding:12 }}>
                        <div style={{ fontSize:9, color:meta.color, fontWeight:700, marginBottom:2 }}>{p.category}</div>
                        <div style={{ fontWeight:700, fontSize:13, color:"#1a1a2e", marginBottom:6 }}>{p.name}</div>
                        <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:10 }}>
                          <span style={{ fontWeight:800, fontSize:14, color:"#1a1a2e" }}>₹{p.price?.toLocaleString("en-IN")}</span>
                          <span style={{ fontSize:11, color:"#aaa", textDecoration:"line-through" }}>₹{p.mrp?.toLocaleString("en-IN")}</span>
                        </div>
                        <div style={{ display:"flex", gap:6 }}>
                          <button onClick={()=>openEdit(p)} style={{ flex:1, padding:"6px", background:"#1a1a2e", color:"white", border:"none", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer" }}>Edit</button>
                          <button onClick={()=>{setProducts(ps=>ps.map(x=>x.id===p.id?{...x,stock:p.stock>0?0:10}:x));notify(p.stock>0?"Marked out":"Restocked");}} style={{ padding:"6px 10px", background:p.stock>0?"#FEF2F2":"#F0FDF4", color:p.stock>0?"#EF4444":"#16A34A", border:"none", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer" }}>{p.stock>0?"Out":"In"}</button>
                          <button onClick={()=>{if(window.confirm(`Delete "${p.name}"?`)){setProducts(ps=>ps.filter(x=>x.id!==p.id));notify("Deleted");}}} style={{ padding:"6px 8px", background:"#FEF2F2", color:"#EF4444", border:"none", borderRadius:8, fontSize:11, cursor:"pointer" }}>🗑</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* FEATURED */}
          {tab==="featured"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:8 }}>Featured Products</h2>
              <p style={{ fontSize:13, color:"#999", marginBottom:20 }}>Homepage "⭐ Featured" section mein yeh products dikhenge.</p>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:12 }}>
                {products.map(p=>{
                  const isFeat = featuredIds.includes(p.id);
                  const meta = CAT_META[p.category]||{color:"#EC4899",light:"#FDF2F8",emoji:"👗"};
                  return (
                    <div key={p.id} style={{ background:"white", borderRadius:14, overflow:"hidden", boxShadow:"0 2px 12px rgba(0,0,0,0.06)", border:`2px solid ${isFeat?"#F59E0B":"#f0f0f0"}` }}>
                      <div style={{ height:130, background:p.img?"white":meta.light, position:"relative", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center" }}>
                        {p.img?<img src={p.img} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }} alt=""/>:<span style={{ fontSize:32, opacity:0.2 }}>{meta.emoji}</span>}
                        {isFeat&&<div style={{ position:"absolute", top:8, right:8, background:"#F59E0B", color:"white", fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:20 }}>⭐ Featured</div>}
                      </div>
                      <div style={{ padding:11 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:"#1a1a2e", marginBottom:6 }}>{p.name}</div>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                          <span style={{ fontWeight:800, color:"#1a1a2e", fontSize:13 }}>₹{p.price?.toLocaleString("en-IN")}</span>
                          <button onClick={()=>setFeaturedIds(ids=>isFeat?ids.filter(x=>x!==p.id):[...ids,p.id])}
                            style={{ padding:"5px 12px", background:isFeat?"#FEF3C7":"#f5f5f5", color:isFeat?"#D97706":"#666", border:"none", borderRadius:20, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                            {isFeat?"★ Remove":"☆ Feature"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* COLLECTIONS — multiple named homepage carousels (New Arrivals, Price-Wise, etc.), admin controls title/visibility/items */}
          {tab==="collections"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:8 }}>Homepage Collections</h2>
              <p style={{ fontSize:13, color:"#999", marginBottom:20 }}>Har collection ek alag horizontal-scroll section banata hai homepage par (jaise "New Arrivals", "Price-Wise Deals" waghera). Title, dikhna/na dikhna, aur usme kaunse products honge — sab yaha se control karo.</p>

              <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:18 }}>
                {homeCollections.map((col,i)=>{
                  const isOpen = expandedCol===col.id;
                  const count = col.productIds.length;
                  return (
                    <div key={col.id} style={{ background:"white", borderRadius:14, overflow:"hidden", boxShadow:"0 2px 12px rgba(0,0,0,0.06)", border:`1px solid ${col.active?col.accent+"33":"#f0f0f0"}` }}>
                      <div style={{ padding:"14px 14px 8px", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                        <div style={{ width:4, height:28, background:col.accent||"#EC4899", borderRadius:2, flexShrink:0 }}/>
                        <input value={col.title} onChange={e=>setHomeCollections(cs=>cs.map((x,j)=>j===i?{...x,title:e.target.value}:x))}
                          style={{ ...inp, flex:1, minWidth:160, fontWeight:700 }} placeholder="Collection title"/>
                        <select value={col.accent||"#EC4899"} onChange={e=>setHomeCollections(cs=>cs.map((x,j)=>j===i?{...x,accent:e.target.value}:x))}
                          style={{ ...inp, width:"auto", padding:"9px 10px" }}>
                          {ACCENT_OPTIONS.map(c=><option key={c} value={c} style={{background:c}}>{c}</option>)}
                        </select>
                        <span style={{ fontSize:11, color:"#999", whiteSpace:"nowrap" }}>{count} item{count!==1?"s":""}</span>
                        <button onClick={()=>setHomeCollections(cs=>cs.map((x,j)=>j===i?{...x,active:!x.active}:x))}
                          style={{ padding:"6px 12px", background:col.active?"#F0FDF4":"#FEF2F2", color:col.active?"#16A34A":"#EF4444", border:"none", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                          {col.active?"✓ Showing":"✗ Hidden"}
                        </button>
                        <button onClick={()=>setHomeCollections(cs=>{const n=[...cs];if(i>0){[n[i],n[i-1]]=[n[i-1],n[i]];}return n;})} style={{ padding:"6px 10px", background:"#fafafa", border:"1px solid #e5e5e5", borderRadius:8, fontSize:11, cursor:"pointer" }}>↑</button>
                        <button onClick={()=>setHomeCollections(cs=>{const n=[...cs];if(i<cs.length-1){[n[i],n[i+1]]=[n[i+1],n[i]];}return n;})} style={{ padding:"6px 10px", background:"#fafafa", border:"1px solid #e5e5e5", borderRadius:8, fontSize:11, cursor:"pointer" }}>↓</button>
                        <button onClick={()=>setExpandedCol(isOpen?null:col.id)} style={{ padding:"6px 14px", background:"#1a1a2e", color:"white", border:"none", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                          {isOpen?"Close":"Pick Products"}
                        </button>
                        <button onClick={()=>{if(window.confirm(`Delete collection "${col.title}"?`))setHomeCollections(cs=>cs.filter(x=>x.id!==col.id));}} style={{ padding:"6px 10px", background:"#FEF2F2", color:"#EF4444", border:"none", borderRadius:8, fontSize:11, cursor:"pointer" }}>🗑</button>
                      </div>

                      <div style={{ padding:"0 14px 14px", display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:10, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1, whiteSpace:"nowrap" }}>Card Style:</span>
                        <select value={col.style||"wave"} onChange={e=>setHomeCollections(cs=>cs.map((x,j)=>j===i?{...x,style:e.target.value}:x))}
                          style={{ ...inp, flex:1, maxWidth:420 }}>
                          {STYLE_OPTIONS.map(s=><option key={s.id} value={s.id}>{s.l}</option>)}
                        </select>
                      </div>

                      {isOpen&&(
                        <div style={{ borderTop:"1px solid #f0f0f0", padding:14, background:"#fafafa" }}>
                          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:8, maxHeight:340, overflowY:"auto" }}>
                            {products.map(p=>{
                              const checked = col.productIds.includes(p.id);
                              const meta = CAT_META[p.category]||{color:"#EC4899",light:"#FDF2F8",emoji:"👗"};
                              return (
                                <label key={p.id} style={{ display:"flex", alignItems:"center", gap:8, background:"white", border:`1.5px solid ${checked?col.accent:"#e5e5e5"}`, borderRadius:10, padding:8, cursor:"pointer" }}>
                                  <input type="checkbox" checked={checked} onChange={()=>toggleColProduct(col.id,p.id)} style={{ accentColor:col.accent, flexShrink:0 }}/>
                                  <div style={{ width:32, height:38, borderRadius:6, overflow:"hidden", flexShrink:0, background:meta.light }}>
                                    {p.img?<img src={p.img} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }} alt="" onError={e=>e.target.style.display="none"}/>:<div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>{meta.emoji}</div>}
                                  </div>
                                  <div style={{ minWidth:0 }}>
                                    <div style={{ fontSize:11, fontWeight:700, color:"#1a1a2e", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</div>
                                    <div style={{ fontSize:10, color:"#999" }}>₹{p.price}</div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <button onClick={addCollection} style={{ background:"white", border:"2px dashed #EC4899", borderRadius:12, padding:"12px 24px", fontSize:13, fontWeight:700, color:"#EC4899", cursor:"pointer" }}>
                + Add Collection
              </button>
            </div>
          )}

          {/* BANNERS */}
          {tab==="banners"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:8 }}>Banners</h2>

              <div style={{ background:"white", borderRadius:14, padding:18, boxShadow:"0 2px 12px rgba(0,0,0,0.05)", marginBottom:24 }}>
                <div style={{ fontSize:14, fontWeight:700, color:"#1a1a2e", marginBottom:4 }}>Site Logo</div>
                <p style={{ fontSize:12, color:"#999", marginBottom:12 }}>
                  PNG photo ko pehle Cloudinary (ya jo bhi use karte ho) pe upload karo, uska direct link yaha paste karo.
                </p>
                <label style={lbl}>Logo URL</label>
                <input value={logoUrl} onChange={e=>setLogoUrl(e.target.value)} style={inp} placeholder="https://... (.png)"/>
                {logoUrl && (
                  <div style={{ marginTop:12, background:"#1a1a2e", borderRadius:10, padding:16, display:"flex", justifyContent:"center" }}>
                    <img src={logoUrl} alt="" style={{ height:56, objectFit:"contain" }} onError={e=>e.target.style.opacity=0.15}/>
                  </div>
                )}
              </div>

              <div style={{ background:"white", borderRadius:14, padding:18, boxShadow:"0 2px 12px rgba(0,0,0,0.05)", marginBottom:24 }}>
                <div style={{ fontSize:14, fontWeight:700, color:"#1a1a2e", marginBottom:4 }}>📱 Order Notification WhatsApp Number</div>
                <p style={{ fontSize:12, color:"#999", marginBottom:12 }}>
                  Naye order ka message, "Contact Us" button, "Track Order" — sab jagah yehi number use hoga. 91 se shuru karke poora number likho, bina + ya space ke.
                </p>
                <label style={lbl}>WhatsApp Number</label>
                <input value={adminWhatsApp} onChange={e=>setAdminWhatsApp(e.target.value)} style={inp} placeholder="918766584266" maxLength={12}/>
                {adminWhatsApp && (
                  <div style={{ marginTop:10, fontSize:12, color:"#16A34A", fontWeight:600 }}>
                    ✓ Preview: <a href={`https://wa.me/${adminWhatsApp}`} target="_blank" rel="noreferrer" style={{ color:"#16A34A" }}>wa.me/{adminWhatsApp}</a> — is link ko khol ke check kar lo sahi number/account khul raha hai
                  </div>
                )}
              </div>

              <div style={{ background:"white", borderRadius:14, padding:18, boxShadow:"0 2px 12px rgba(0,0,0,0.05)", marginBottom:24 }}>
                <div style={{ fontSize:14, fontWeight:700, color:"#1a1a2e", marginBottom:4 }}>🚚 Delivery Charges</div>
                <p style={{ fontSize:12, color:"#999", marginBottom:12 }}>
                  Kitne amount ke upar order pe delivery FREE ho, aur usse kam pe kitna delivery charge lage. Testing ke liye "Free above" 0 kar sakte ho (sabhi orders free delivery).
                </p>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  <div>
                    <label style={lbl}>Free Delivery Above ₹</label>
                    <input type="number" min={0} value={freeShipThreshold} onChange={e=>setFreeShipThreshold(e.target.value)} style={inp} placeholder="999"/>
                  </div>
                  <div>
                    <label style={lbl}>Delivery Charge ₹ (agar threshold se kam ho)</label>
                    <input type="number" min={0} value={shippingFee} onChange={e=>setShippingFee(e.target.value)} style={inp} placeholder="49"/>
                  </div>
                </div>
                <div style={{ marginTop:10, fontSize:11, color:"#999" }}>
                  Abhi: ₹{freeShipThreshold} se kam order pe ₹{shippingFee} delivery charge lagega, usse zyada pe FREE.
                </div>
              </div>

              <div style={{ background:"white", borderRadius:14, padding:18, boxShadow:"0 2px 12px rgba(0,0,0,0.05)", marginBottom:24 }}>
                <div style={{ fontSize:14, fontWeight:700, color:"#1a1a2e", marginBottom:4 }}>Homepage Top Banner</div>
                <p style={{ fontSize:12, color:"#999", marginBottom:16 }}>
                  Sirf photo — koi text/button nahi hoga, poori photo bina crop kiye dikhegi. Desktop aur Mobile ke liye alag-alag photo daal sakte ho.
                </p>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }} className="hero-photo-admin-grid">
                  <div>
                    <label style={lbl}>🖥️ Desktop / PC Photo</label>
                    <input value={heroPhotoDesktop} onChange={e=>setHeroPhotoDesktop(e.target.value)} style={inp} placeholder="https://... (wide photo)"/>
                    {heroPhotoDesktop && (
                      <div style={{ marginTop:10, borderRadius:10, overflow:"hidden", border:"1px solid #f0f0f0" }}>
                        <img src={heroPhotoDesktop} alt="" style={{ width:"100%", display:"block" }} onError={e=>e.target.style.opacity=0.15}/>
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={lbl}>📱 Mobile Photo</label>
                    <input value={heroPhotoMobile} onChange={e=>setHeroPhotoMobile(e.target.value)} style={inp} placeholder="https://... (tall/square photo)"/>
                    {heroPhotoMobile && (
                      <div style={{ marginTop:10, borderRadius:10, overflow:"hidden", border:"1px solid #f0f0f0", maxWidth:220 }}>
                        <img src={heroPhotoMobile} alt="" style={{ width:"100%", display:"block" }} onError={e=>e.target.style.opacity=0.15}/>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:18, fontWeight:900, color:"#1a1a2e", marginBottom:16 }}>Other Sliding Banners</h3>
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                {banners.map((b,i)=>(
                  <div key={b.id} style={{ background:"white", borderRadius:14, overflow:"hidden", boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}>
                    <div style={{ height:88, background:b.bg, display:"flex", alignItems:"center", padding:"0 22px", position:"relative" }}>
                      <div>
                        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:17, fontWeight:900, color:"white" }}>{b.title}</div>
                        <div style={{ fontSize:12, color:"rgba(255,255,255,0.75)" }}>{b.subtitle}</div>
                      </div>
                    </div>
                    {bannerEdit===i?(
                      <div style={{ padding:18 }}>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
                          {[["title","Banner Title"],["subtitle","Subtitle"],["cta","Button Text"],["img","Image URL"],["bg","Background CSS"]].map(([k,pl])=>(
                            <div key={k} style={{ gridColumn:k==="bg"||k==="img"?"1/3":"auto" }}>
                              <label style={lbl}>{pl}</label>
                              <input value={b[k]||""} onChange={e=>setBanners(bs=>bs.map((x,j)=>j===i?{...x,[k]:e.target.value}:x))} style={inp} placeholder={pl}/>
                            </div>
                          ))}
                        </div>
                        <button onClick={()=>{setBannerEdit(null);notify("Saved!");}} style={{ background:"linear-gradient(135deg,#EC4899,#7C3AED)", color:"white", border:"none", padding:"8px 22px", borderRadius:50, fontSize:12, fontWeight:700, cursor:"pointer" }}>Save</button>
                      </div>
                    ):(
                      <div style={{ padding:"10px 18px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <span style={{ fontSize:13, fontWeight:600 }}>Banner {i+1}</span>
                        <div style={{ display:"flex", gap:6 }}>
                          <button onClick={()=>setBanners(bs=>{const n=[...bs];if(i>0){[n[i],n[i-1]]=[n[i-1],n[i]];}return n;})} style={{ padding:"5px 10px", background:"#fafafa", border:"1px solid #e5e5e5", borderRadius:8, fontSize:11, cursor:"pointer" }}>↑</button>
                          <button onClick={()=>setBanners(bs=>{const n=[...bs];if(i<bs.length-1){[n[i],n[i+1]]=[n[i+1],n[i]];}return n;})} style={{ padding:"5px 10px", background:"#fafafa", border:"1px solid #e5e5e5", borderRadius:8, fontSize:11, cursor:"pointer" }}>↓</button>
                          <button onClick={()=>setBannerEdit(i)} style={{ padding:"5px 12px", background:"#1a1a2e", color:"white", border:"none", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer" }}>Edit</button>
                          <button onClick={()=>{if(banners.length>1)setBanners(bs=>bs.filter((_,j)=>j!==i));}} style={{ padding:"5px 10px", background:"#FEF2F2", color:"#EF4444", border:"none", borderRadius:8, fontSize:11, cursor:"pointer" }}>🗑</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <button onClick={()=>setBanners(bs=>[...bs,{id:Date.now(),title:"New Banner",subtitle:"Subtitle",cta:"Shop Now",bg:"linear-gradient(135deg,#667eea,#764ba2)",img:""}])}
                  style={{ background:"white", border:"2px dashed #e5e5e5", borderRadius:14, padding:"14px", fontSize:13, fontWeight:700, color:"#999", cursor:"pointer" }}>
                  + Add Banner
                </button>
              </div>
            </div>
          )}

          {/* CATEGORY STRIP */}
          {tab==="catstrip"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:8 }}>Category Strip</h2>
              <p style={{ fontSize:13, color:"#999", marginBottom:20 }}>Homepage auto-scroll strip manage karo.</p>
              <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:18 }}>
                {catStrip.map((cat,i)=>(
                  <div key={cat.id} style={{ background:"white", borderRadius:12, padding:14, border:`1px solid ${cat.active?"#EC489922":"#f0f0f0"}`, display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>
                    <div style={{ width:56, height:68, borderRadius:10, overflow:"hidden", flexShrink:0, border:"1px solid #f0f0f0", background:"#fafafa" }}>
                      {cat.img?<img src={cat.img} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }} alt="" onError={e=>e.target.style.display="none"}/>:<div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, opacity:0.3 }}>{(CAT_META[cat.category]||{emoji:"👗"}).emoji}</div>}
                    </div>
                    <div style={{ flex:1, display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, minWidth:200 }}>
                      <div><label style={lbl}>Name</label><input value={cat.name} onChange={e=>setCatStrip(cs=>cs.map((x,j)=>j===i?{...x,name:e.target.value}:x))} style={inp}/></div>
                      <div><label style={lbl}>Category</label>
                        <select value={cat.category} onChange={e=>setCatStrip(cs=>cs.map((x,j)=>j===i?{...x,category:e.target.value}:x))} style={inp}>
                          {categories.map(c=><option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div style={{ gridColumn:"1/3" }}><label style={lbl}>Photo URL</label><input value={cat.img} onChange={e=>setCatStrip(cs=>cs.map((x,j)=>j===i?{...x,img:e.target.value}:x))} style={inp} placeholder="https://..."/></div>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      <button onClick={()=>setCatStrip(cs=>cs.map((x,j)=>j===i?{...x,active:!x.active}:x))}
                        style={{ padding:"5px 12px", background:cat.active?"#F0FDF4":"#FEF2F2", color:cat.active?"#16A34A":"#EF4444", border:"none", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                        {cat.active?"✓ Show":"✗ Hide"}
                      </button>
                      <button onClick={()=>setCatStrip(cs=>cs.filter((_,j)=>j!==i))} style={{ padding:"5px 10px", background:"#FEF2F2", color:"#EF4444", border:"none", borderRadius:8, fontSize:11, cursor:"pointer" }}>🗑</button>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={()=>setCatStrip(cs=>[...cs,{id:Date.now(),name:"New",category:CATEGORIES[0],img:"",active:true}])}
                style={{ background:"white", border:"2px dashed #EC4899", borderRadius:12, padding:"12px 24px", fontSize:13, fontWeight:700, color:"#EC4899", cursor:"pointer", marginBottom:14, display:"block" }}>
                + Add Item
              </button>
              {/* Category grid photos */}
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:18, fontWeight:700, color:"#1a1a2e", margin:"24px 0 14px" }}>Category Grid Photos</h3>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:12 }}>
                {categories.map(cat=>{
                  const m = getMeta(cat);
                  return (
                    <div key={cat} style={{ background:"white", borderRadius:12, padding:14, border:"1px solid #f0f0f0", display:"flex", gap:10, alignItems:"center" }}>
                      <div style={{ width:54, height:68, borderRadius:10, overflow:"hidden", flexShrink:0, background:m.light, position:"relative" }}>
                        {catPhotos?.[cat]?<img src={catPhotos[cat]} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }} alt="" onError={e=>e.target.style.display="none"}/>:<div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>{m.emoji}</div>}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:11, fontWeight:800, color:m.color, marginBottom:4 }}>{cat}</div>
                        <input value={catPhotos?.[cat]||""} onChange={e=>setCatPhotos(cp=>({...cp,[cat]:e.target.value}))} placeholder="Image URL..." style={{ width:"100%", padding:"6px 10px", border:"1px solid #e5e5e5", borderRadius:8, fontSize:11, outline:"none" }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button onClick={()=>notify("Saved!")} style={{ background:"linear-gradient(135deg,#EC4899,#7C3AED)", color:"white", border:"none", padding:"11px 28px", borderRadius:50, fontSize:13, fontWeight:800, cursor:"pointer", marginTop:14 }}>💾 Save All</button>
            </div>
          )}

          {/* OCCASION */}
          {tab==="occasion"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:8 }}>Occasion Collections</h2>
              <p style={{ fontSize:13, color:"#999", marginBottom:20 }}>Homepage ke neeche large image tiles (jaise "Office Wear", "Festive Edit") — click karne par us category ka shop page khulta hai.</p>
              <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:18 }}>
                {occasionTiles.map((t,i)=>(
                  <div key={t.id} style={{ background:"white", borderRadius:14, padding:16, border:`1px solid ${t.active?"#EC489922":"#f0f0f0"}`, display:"flex", gap:14, alignItems:"center", flexWrap:"wrap" }}>
                    <div style={{ width:70, height:88, borderRadius:10, overflow:"hidden", flexShrink:0, background:"#fafafa", border:"1px solid #f0f0f0" }}>
                      {t.img?<img src={t.img} style={{ width:"100%", height:"100%", objectFit:"cover" }} alt="" onError={e=>e.target.style.display="none"}/>:<div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, opacity:0.3 }}>🎉</div>}
                    </div>
                    <div style={{ flex:1, display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, minWidth:240 }}>
                      <div><label style={lbl}>Title</label><input value={t.title} onChange={e=>setOccasionTiles(os=>os.map((x,j)=>j===i?{...x,title:e.target.value}:x))} style={inp}/></div>
                      <div><label style={lbl}>Links to Category</label>
                        <select value={t.category} onChange={e=>setOccasionTiles(os=>os.map((x,j)=>j===i?{...x,category:e.target.value}:x))} style={inp}>
                          {categories.map(c=><option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div style={{ gridColumn:"1/3" }}><label style={lbl}>Photo URL</label><input value={t.img} onChange={e=>setOccasionTiles(os=>os.map((x,j)=>j===i?{...x,img:e.target.value}:x))} style={inp} placeholder="https://..."/></div>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      <button onClick={()=>setOccasionTiles(os=>os.map((x,j)=>j===i?{...x,active:!x.active}:x))}
                        style={{ padding:"6px 12px", background:t.active?"#F0FDF4":"#FEF2F2", color:t.active?"#16A34A":"#EF4444", border:"none", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                        {t.active?"✓ Show":"✗ Hide"}
                      </button>
                      <button onClick={()=>setOccasionTiles(os=>os.filter((_,j)=>j!==i))} style={{ padding:"6px 10px", background:"#FEF2F2", color:"#EF4444", border:"none", borderRadius:8, fontSize:11, cursor:"pointer" }}>🗑</button>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={()=>setOccasionTiles(os=>[...os,{id:Date.now(),title:"New Collection",category:CATEGORIES[0],img:"",active:true}])}
                style={{ background:"white", border:"2px dashed #EC4899", borderRadius:12, padding:"12px 24px", fontSize:13, fontWeight:700, color:"#EC4899", cursor:"pointer" }}>
                + Add Occasion Tile
              </button>
            </div>
          )}

          {/* CATEGORIES — add/remove product categories */}
          {tab==="categories"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:8 }}>Categories</h2>
              <p style={{ fontSize:13, color:"#999", marginBottom:20 }}>Naya category add karo — products, Shop filter, Category Strip, Occasion tiles — sab jagah automatically dikhega.</p>

              <div style={{ background:"white", borderRadius:14, padding:18, boxShadow:"0 2px 12px rgba(0,0,0,0.05)", marginBottom:20 }}>
                <div style={{ fontSize:12, fontWeight:800, color:"#1a1a2e", marginBottom:12 }}>+ Add New Category</div>
                <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"flex-end" }}>
                  <div style={{ flex:1, minWidth:160 }}>
                    <label style={lbl}>Category Name</label>
                    <input value={newCat.name} onChange={e=>setNewCat({...newCat,name:e.target.value})} style={inp} placeholder="e.g. Sarees"/>
                  </div>
                  <div style={{ width:90 }}>
                    <label style={lbl}>Emoji</label>
                    <input value={newCat.emoji} onChange={e=>setNewCat({...newCat,emoji:e.target.value})} style={inp} placeholder="👗"/>
                  </div>
                  <div style={{ width:110 }}>
                    <label style={lbl}>Color</label>
                    <select value={newCat.color} onChange={e=>setNewCat({...newCat,color:e.target.value})} style={inp}>
                      {ACCENT_OPTIONS.map(c=><option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <button onClick={()=>{ if(addCategory(newCat.name,newCat.emoji,newCat.color)) setNewCat({name:"",emoji:"👗",color:"#EC4899"}); }}
                    style={{ background:"linear-gradient(135deg,#EC4899,#7C3AED)", color:"white", border:"none", padding:"11px 24px", borderRadius:12, fontSize:13, fontWeight:700, cursor:"pointer" }}>
                    Add
                  </button>
                </div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:12 }}>
                {categories.map(cat=>{
                  const m = getMeta(cat);
                  const count = products.filter(p=>p.category===cat).length;
                  return (
                    <div key={cat} style={{ background:"white", borderRadius:14, padding:16, border:"1px solid #f0f0f0", boxShadow:"0 2px 12px rgba(0,0,0,0.05)", display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ width:40, height:40, borderRadius:10, background:m.light, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{m.emoji}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:13, color:"#1a1a2e" }}>{cat}</div>
                        <div style={{ fontSize:11, color:"#999" }}>{count} product{count!==1?"s":""}</div>
                      </div>
                      <button onClick={()=>removeCategory(cat)} style={{ padding:"6px 10px", background:"#FEF2F2", color:"#EF4444", border:"none", borderRadius:8, fontSize:11, cursor:"pointer" }}>🗑</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* COUPONS */}
          {tab==="coupons"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:8 }}>Coupons</h2>
              <p style={{ fontSize:13, color:"#999", marginBottom:20 }}>Naya coupon add karo — checkout par turant use ho sakega (server-side bhi verify hota hai, safe hai).</p>

              <div style={{ background:"white", borderRadius:14, padding:18, boxShadow:"0 2px 12px rgba(0,0,0,0.05)", marginBottom:20 }}>
                <div style={{ fontSize:12, fontWeight:800, color:"#1a1a2e", marginBottom:12 }}>+ Add New Coupon</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))", gap:10, marginBottom:10 }}>
                  <div><label style={lbl}>Code</label><input value={newCoupon.code} onChange={e=>setNewCoupon({...newCoupon,code:e.target.value.toUpperCase()})} style={inp} placeholder="SAVE100"/></div>
                  <div><label style={lbl}>Type</label>
                    <select value={newCoupon.type} onChange={e=>setNewCoupon({...newCoupon,type:e.target.value})} style={inp}>
                      <option value="percent">% Percent</option>
                      <option value="flat">₹ Flat</option>
                    </select>
                  </div>
                  <div><label style={lbl}>Value</label><input type="number" value={newCoupon.value} onChange={e=>setNewCoupon({...newCoupon,value:e.target.value})} style={inp} placeholder={newCoupon.type==="percent"?"10":"100"}/></div>
                  <div><label style={lbl}>Min Order ₹</label><input type="number" value={newCoupon.minOrder} onChange={e=>setNewCoupon({...newCoupon,minOrder:e.target.value})} style={inp} placeholder="499"/></div>
                  <div><label style={lbl}>Max Discount ₹ (optional)</label><input type="number" value={newCoupon.maxDiscount} onChange={e=>setNewCoupon({...newCoupon,maxDiscount:e.target.value})} style={inp} placeholder="Blank = no cap"/></div>
                  <div><label style={lbl}>Label (shown to customer)</label><input value={newCoupon.label} onChange={e=>setNewCoupon({...newCoupon,label:e.target.value})} style={inp} placeholder="10% off"/></div>
                </div>
                <button onClick={()=>{
                  if (!newCoupon.code || !newCoupon.value) { notify("Code aur Value required","err"); return; }
                  setCoupons(cs=>({ ...cs, [newCoupon.code]: {
                    type:newCoupon.type, value:+newCoupon.value, minOrder:+(newCoupon.minOrder||0),
                    maxDiscount:newCoupon.maxDiscount?+newCoupon.maxDiscount:null,
                    label:newCoupon.label||`${newCoupon.type==="percent"?newCoupon.value+"%":"₹"+newCoupon.value} off`,
                    active:true,
                  }}));
                  notify("Coupon add ho gaya!");
                  setNewCoupon({ code:"", type:"percent", value:"", minOrder:"", maxDiscount:"", label:"" });
                }} style={{ background:"linear-gradient(135deg,#EC4899,#7C3AED)", color:"white", border:"none", padding:"10px 24px", borderRadius:12, fontSize:13, fontWeight:700, cursor:"pointer" }}>Add Coupon</button>
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {Object.entries(coupons).map(([code,def])=>(
                  <div key={code} style={{ background:"white", borderRadius:12, padding:16, border:"1px solid #f0f0f0", display:"flex", alignItems:"center", gap:14, flexWrap:"wrap", boxShadow:"0 2px 10px rgba(0,0,0,0.04)" }}>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:900, fontSize:15, color:"#EC4899", minWidth:120 }}>{code}</div>
                    <div style={{ fontSize:12, color:"#666", flex:1, minWidth:180 }}>
                      {def.label} · Min ₹{def.minOrder} {def.maxDiscount?`· Max ₹${def.maxDiscount} off`:""}
                    </div>
                    <button onClick={()=>setCoupons(cs=>({...cs,[code]:{...cs[code],active:cs[code].active===false}}))}
                      style={{ padding:"6px 14px", background:def.active===false?"#FEF2F2":"#F0FDF4", color:def.active===false?"#EF4444":"#16A34A", border:"none", borderRadius:20, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                      {def.active===false?"✗ Inactive":"✓ Active"}
                    </button>
                    <button onClick={()=>{ if(window.confirm(`Delete coupon "${code}"?`)) setCoupons(cs=>{const n={...cs}; delete n[code]; return n;}); }}
                      style={{ padding:"6px 10px", background:"#FEF2F2", color:"#EF4444", border:"none", borderRadius:8, fontSize:11, cursor:"pointer" }}>🗑</button>
                  </div>
                ))}
                {Object.keys(coupons).length===0 && <p style={{ color:"#999", fontSize:13 }}>Koi coupon nahi hai abhi.</p>}
              </div>
            </div>
          )}

          {/* ANNOUNCEMENT BAR */}
          {tab==="announce"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:8 }}>Announcement Bar</h2>
              <p style={{ fontSize:13, color:"#999", marginBottom:20 }}>Homepage ke top wali scrolling ticker me jo messages ghumte hain, wo yaha se edit karo.</p>

              <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:18 }}>
                {announcements.map((a,i)=>(
                  <div key={i} style={{ background:"white", borderRadius:12, padding:12, border:"1px solid #f0f0f0", display:"flex", alignItems:"center", gap:10 }}>
                    <input value={a} onChange={e=>setAnnouncements(list=>list.map((x,j)=>j===i?e.target.value:x))} style={{...inp, flex:1}}/>
                    <button onClick={()=>setAnnouncements(list=>list.filter((_,j)=>j!==i))} style={{ padding:"6px 10px", background:"#FEF2F2", color:"#EF4444", border:"none", borderRadius:8, fontSize:11, cursor:"pointer" }}>🗑</button>
                  </div>
                ))}
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <input value={newAnnouncement} onChange={e=>setNewAnnouncement(e.target.value)} style={{...inp, flex:1}} placeholder="Naya message likho..."
                  onKeyDown={e=>{ if(e.key==="Enter" && newAnnouncement.trim()){ setAnnouncements(list=>[...list,newAnnouncement.trim()]); setNewAnnouncement(""); } }}/>
                <button onClick={()=>{ if(newAnnouncement.trim()){ setAnnouncements(list=>[...list,newAnnouncement.trim()]); setNewAnnouncement(""); } }}
                  style={{ background:"linear-gradient(135deg,#EC4899,#7C3AED)", color:"white", border:"none", padding:"10px 24px", borderRadius:12, fontSize:13, fontWeight:700, cursor:"pointer" }}>Add</button>
              </div>
            </div>
          )}

          {/* VIDEOS */}
          {tab==="videos"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:8 }}>Product Videos</h2>
              <p style={{ fontSize:13, color:"#999", marginBottom:20 }}>
                Video daalo, product link karo, size/shape choose karo, aur homepage ya Shop page pe kaha dikhana he wo bhi choose karo.
              </p>

              <div style={{ background:"white", borderRadius:14, padding:18, boxShadow:"0 2px 12px rgba(0,0,0,0.05)", marginBottom:20 }}>
                <div style={{ fontSize:12, fontWeight:800, color:"#1a1a2e", marginBottom:12 }}>+ Add New Video</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:12, marginBottom:12 }}>
                  <div style={{ gridColumn:"1/-1" }}>
                    <label style={lbl}>Video URL (.mp4 link)</label>
                    <input value={newVideo.videoUrl} onChange={e=>setNewVideo({...newVideo,videoUrl:e.target.value})} style={inp} placeholder="https://.../video.mp4"/>
                  </div>
                  <div>
                    <label style={lbl}>Linked Product</label>
                    <select value={newVideo.productId} onChange={e=>setNewVideo({...newVideo,productId:e.target.value})} style={inp}>
                      <option value="">— Select Product —</option>
                      {products.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Video Shape</label>
                    <select value={newVideo.aspectRatio} onChange={e=>setNewVideo({...newVideo,aspectRatio:e.target.value})} style={inp}>
                      {VIDEO_ASPECTS.map(a=><option key={a.key} value={a.key}>{a.label}</option>)}
                    </select>
                  </div>
                  <div style={{ gridColumn:"1/-1" }}>
                    <label style={lbl}>Where to Show</label>
                    <select value={newVideo.placement} onChange={e=>setNewVideo({...newVideo,placement:e.target.value})} style={inp}>
                      <optgroup label="Homepage — appears right after this section">
                        {HOME_SECTIONS.map(s=><option key={s.key} value={`home:${s.key}`}>After: {s.label}</option>)}
                      </optgroup>
                      <optgroup label="Shop Page — appears at top of this category">
                        {categories.map(c=><option key={c} value={`shop:${c}`}>Shop page: {c}</option>)}
                      </optgroup>
                    </select>
                  </div>
                </div>
                <button onClick={()=>{
                  if (!newVideo.videoUrl) { notify("Video URL required","err"); return; }
                  setVideoShowcases(vs=>[...vs, { id:Date.now(), ...newVideo, productId:newVideo.productId?+newVideo.productId:null, active:true }]);
                  notify("Video add ho gaya!");
                  setNewVideo({ videoUrl:"", productId:"", aspectRatio:"portrait", placement:"home:featured" });
                }} style={{ background:"linear-gradient(135deg,#EC4899,#7C3AED)", color:"white", border:"none", padding:"10px 24px", borderRadius:12, fontSize:13, fontWeight:700, cursor:"pointer" }}>Add Video</button>
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                {videoShowcases.map(v=>{
                  const prod = products.find(p=>p.id===v.productId);
                  const aspectLabel = VIDEO_ASPECTS.find(a=>a.key===v.aspectRatio)?.label || v.aspectRatio;
                  const placementLabel = v.placement?.startsWith("home:")
                    ? "After: " + (HOME_SECTIONS.find(s=>s.key===v.placement.split(":")[1])?.label || v.placement)
                    : "Shop page: " + v.placement?.split(":")[1];
                  return (
                    <div key={v.id} style={{ background:"white", borderRadius:14, padding:16, border:"1px solid #f0f0f0", display:"flex", gap:14, alignItems:"center", flexWrap:"wrap", boxShadow:"0 2px 10px rgba(0,0,0,0.04)" }}>
                      <video src={v.videoUrl} muted style={{ width:56, height:70, objectFit:"cover", borderRadius:8, background:"#000", flexShrink:0 }}/>
                      <div style={{ flex:1, minWidth:200 }}>
                        <div style={{ fontWeight:700, fontSize:13 }}>{prod?.name || "(no product linked)"}</div>
                        <div style={{ fontSize:11, color:"#999" }}>{aspectLabel} · {placementLabel}</div>
                      </div>
                      <button onClick={()=>setVideoShowcases(vs=>vs.map(x=>x.id===v.id?{...x,active:x.active===false?true:false}:x))}
                        style={{ padding:"6px 14px", background:v.active===false?"#FEF2F2":"#F0FDF4", color:v.active===false?"#EF4444":"#16A34A", border:"none", borderRadius:20, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                        {v.active===false?"✗ Hidden":"✓ Showing"}
                      </button>
                      <button onClick={()=>setVideoShowcases(vs=>vs.filter(x=>x.id!==v.id))} style={{ padding:"6px 10px", background:"#FEF2F2", color:"#EF4444", border:"none", borderRadius:8, fontSize:11, cursor:"pointer" }}>🗑</button>
                    </div>
                  );
                })}
                {videoShowcases.length===0 && <p style={{ color:"#999", fontSize:13 }}>Koi video nahi hai abhi.</p>}
              </div>
            </div>
          )}

          {/* KURTI STYLES */}
          {tab==="kurtistyle"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:8 }}>Shop Kurtis by Style</h2>
              <p style={{ fontSize:13, color:"#999", marginBottom:20 }}>Homepage ke "Shop Kurtis by Style" section ke tiles yaha se edit karo.</p>
              <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:18 }}>
                {kurtiStyleTiles.map((t,i)=>(
                  <div key={t.id} style={{ background:"white", borderRadius:14, padding:16, border:"1px solid #f0f0f0", display:"flex", gap:14, alignItems:"center", flexWrap:"wrap" }}>
                    <div style={{ width:60, height:76, borderRadius:10, overflow:"hidden", flexShrink:0, background:"#fafafa", border:"1px solid #f0f0f0" }}>
                      {t.img?<img src={t.img} style={{ width:"100%", height:"100%", objectFit:"cover" }} alt="" onError={e=>e.target.style.display="none"}/>:<div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, opacity:0.3 }}>👗</div>}
                    </div>
                    <div style={{ flex:1, display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, minWidth:220 }}>
                      <div><label style={lbl}>Style Name</label><input value={t.name} onChange={e=>setKurtiStyleTiles(ks=>ks.map((x,j)=>j===i?{...x,name:e.target.value}:x))} style={inp}/></div>
                      <div><label style={lbl}>"From" Price</label><input value={t.from} onChange={e=>setKurtiStyleTiles(ks=>ks.map((x,j)=>j===i?{...x,from:e.target.value}:x))} style={inp} placeholder="₹999"/></div>
                      <div style={{ gridColumn:"1/3" }}><label style={lbl}>Photo URL</label><input value={t.img} onChange={e=>setKurtiStyleTiles(ks=>ks.map((x,j)=>j===i?{...x,img:e.target.value}:x))} style={inp} placeholder="https://..."/></div>
                    </div>
                    <button onClick={()=>setKurtiStyleTiles(ks=>ks.filter((_,j)=>j!==i))} style={{ padding:"6px 10px", background:"#FEF2F2", color:"#EF4444", border:"none", borderRadius:8, fontSize:11, cursor:"pointer" }}>🗑</button>
                  </div>
                ))}
              </div>
              <button onClick={()=>setKurtiStyleTiles(ks=>[...ks,{id:Date.now(),name:"New Style",img:"",from:"₹999"}])}
                style={{ background:"white", border:"2px dashed #EC4899", borderRadius:12, padding:"12px 24px", fontSize:13, fontWeight:700, color:"#EC4899", cursor:"pointer" }}>
                + Add Style Tile
              </button>
            </div>
          )}

          {/* ORDERS */}
          {tab==="orders"&&(
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:10 }}>
                <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e" }}>Orders ({orders.length})</h2>
                <input value={orderSearchQ} onChange={e=>setOrderSearchQ(e.target.value)} placeholder="Search by Order ID or name..." style={{ padding:"8px 14px", border:"1px solid #e5e5e5", borderRadius:50, fontSize:12, outline:"none", width:220 }}/>
              </div>
              {(() => {
                const q = orderSearchQ.trim().toLowerCase();
                const filteredOrders = q ? orders.filter(o =>
                  o.id?.toLowerCase().includes(q) ||
                  o.addr?.name?.toLowerCase().includes(q) ||
                  o.addr?.phone?.includes(q)
                ) : orders;
                if (!filteredOrders.length) return <p style={{ color:"#999", fontSize:13 }}>{q ? "Koi order nahi mila." : "No orders yet."}</p>;
                return filteredOrders.map(o=>(
                  <div key={o.id} style={{ background:"white", borderRadius:14, padding:18, marginBottom:10, boxShadow:"0 2px 12px rgba(0,0,0,0.05)" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:10, marginBottom:10 }}>
                      <div><div style={{ fontWeight:800, fontSize:14 }}>{o.id}</div><div style={{ fontSize:11, color:"#999" }}>{o.addr?.name} · {o.addr?.phone} · {o.date}</div></div>
                      <div style={{ textAlign:"right" }}><div style={{ fontWeight:900, fontSize:16 }}>₹{o.total?.toLocaleString("en-IN")}</div><span style={{ fontSize:10, fontWeight:700, background:"#F0FDF4", color:"#16A34A", padding:"2px 8px", borderRadius:20 }}>{o.status}</span></div>
                    </div>
                    <div style={{ fontSize:11, color:"#999", marginBottom:8 }}>{o.items?.length} item(s) · {o.pay?.toUpperCase()}</div>
                    <a href={`https://wa.me/91${(o.addr?.phone||"").replace(/\D/g,"").slice(-10)}?text=${encodeURIComponent(`Hi ${o.addr?.name}, this is Matchwell regarding your order ${o.id}.`)}`} target="_blank" rel="noreferrer" style={{ fontSize:12, color:"#16A34A", fontWeight:700, textDecoration:"none" }}>💬 WhatsApp Customer</a>
                  </div>
                ));
              })()}
            </div>
          )}

          {/* CUSTOMERS */}
          {tab==="customers"&&(
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, flexWrap:"wrap", gap:10 }}>
                <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e" }}>Customers {customers?`(${customers.length})`:""}</h2>
                <button onClick={loadCustomers} disabled={customersLoading} style={{ background:"linear-gradient(135deg,#EC4899,#7C3AED)", color:"white", border:"none", padding:"9px 20px", borderRadius:50, fontSize:12, fontWeight:700, cursor:customersLoading?"not-allowed":"pointer", opacity:customersLoading?0.6:1 }}>
                  {customersLoading?"Loading...":customers?"↻ Refresh":"Load Customers"}
                </button>
              </div>
              <p style={{ fontSize:13, color:"#999", marginBottom:20 }}>Registered customer accounts (guest checkout wale is list me nahi aayenge).</p>

              {customersError && (
                <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:10, padding:"12px 16px", fontSize:12, color:"#EF4444", marginBottom:16 }}>
                  {customersError}
                </div>
              )}

              {customers && (
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {customers.map(c=>(
                    <div key={c.id} style={{ background:"white", borderRadius:12, padding:14, border:"1px solid #f0f0f0", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                      <div style={{ width:36, height:36, borderRadius:"50%", background:"linear-gradient(135deg,#EC4899,#7C3AED)", display:"flex", alignItems:"center", justifyContent:"center", color:"white", fontWeight:800, flexShrink:0 }}>
                        {(c.name||"?")[0].toUpperCase()}
                      </div>
                      <div style={{ flex:1, minWidth:180 }}>
                        <div style={{ fontWeight:700, fontSize:13 }}>{c.name}</div>
                        <div style={{ fontSize:11, color:"#999" }}>{c.email} {c.phone?`· ${c.phone}`:""}</div>
                      </div>
                      <div style={{ fontSize:10, color:"#999", textAlign:"right" }}>
                        Joined {new Date(c.createdAt).toLocaleDateString("en-IN")}
                      </div>
                    </div>
                  ))}
                  {customers.length===0 && <p style={{ color:"#999", fontSize:13 }}>Abhi tak koi registered customer nahi hai.</p>}
                </div>
              )}
            </div>
          )}

          {/* PAYMENT */}
          {tab==="payment"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:8 }}>Payments</h2>
              <p style={{ fontSize:13, color:"#999", marginBottom:20 }}>Customer payments is site par kaise chalte hain.</p>

              <div style={{ background:"white", borderRadius:16, padding:22, boxShadow:"0 2px 12px rgba(0,0,0,0.05)", marginBottom:18 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
                  <span style={{ fontSize:22 }}>💳</span>
                  <h3 style={{ fontSize:16, fontWeight:700, color:"#1a1a2e" }}>Razorpay se sab automatic hai</h3>
                </div>
                <p style={{ fontSize:13, color:"#666", lineHeight:1.7, marginBottom:14 }}>
                  Customer checkout karta hai to UPI, Card, ya Net Banking — sab Razorpay ke secure widget se hota hai.
                  Paisa seedha aapke Razorpay account me aata hai, phir wahan se aapke bank account me settle hota hai
                  (usually 1-2 din me). Cash on Delivery abhi is site par offer nahi kiya ja raha.
                </p>
                <a href="https://dashboard.razorpay.com" target="_blank" rel="noreferrer"
                  style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#1a1a2e", color:"white", padding:"10px 22px", borderRadius:50, fontSize:12, fontWeight:700, textDecoration:"none" }}>
                  Razorpay Dashboard kholo ↗
                </a>
              </div>

              <div style={{ background:"white", borderRadius:16, padding:22, boxShadow:"0 2px 12px rgba(0,0,0,0.05)" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
                  <span style={{ fontSize:22 }}>🏦</span>
                  <h3 style={{ fontSize:16, fontWeight:700, color:"#1a1a2e" }}>Settlement bank account badalna ho</h3>
                </div>
                <p style={{ fontSize:13, color:"#666", lineHeight:1.7 }}>
                  Aapka bank account jaha paisa settle hota hai, wo yaha se nahi — <strong>Razorpay Dashboard → Account & Settings → Bank Accounts</strong> se
                  manage hota hai. Ye zyada secure hai kyunki bank details kabhi is website ke code/database me store nahi hoti.
                </p>
              </div>
            </div>
          )}

          {/* STAFF */}
          {tab==="staff"&&(
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:900, color:"#1a1a2e", marginBottom:20 }}>Staff Accounts</h2>
              <div style={{ background:"#FFF7ED", border:"1px solid #FED7AA", borderRadius:10, padding:"10px 14px", fontSize:12, color:"#EA580C", marginBottom:16, fontWeight:600 }}>Naya staff account add karne ke liye Supabase Dashboard → Authentication → Users me banao, phir SQL Editor se role "admin" tag karo.</div>
              {[{email:"mis@ematchwell.com",name:"MIS Admin",role:"admin"},{email:"mdo@ematchwell.com",name:"MDO Admin",role:"admin"}].map((u,i)=>(
                <div key={i} style={{ background:"white", borderRadius:14, padding:18, marginBottom:10, display:"flex", justifyContent:"space-between", alignItems:"center", boxShadow:"0 2px 12px rgba(0,0,0,0.05)" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ width:40, height:40, background:"linear-gradient(135deg,#EC4899,#7C3AED)", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", color:"white", fontWeight:800 }}>{u.name[0]}</div>
                    <div><div style={{ fontWeight:700, fontSize:14 }}>{u.name}</div><div style={{ fontSize:12, color:"#999" }}>{u.email}</div></div>
                  </div>
                  <span style={{ fontSize:10, fontWeight:700, background:"#F5F3FF", color:"#7C3AED", padding:"4px 14px", borderRadius:20, textTransform:"uppercase" }}>{u.role}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
